import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import {
  Text,
  FormControl,
  FormLabel,
  Button,
  Flex,
  useToast,
  Input,
  Tooltip,
} from '@chakra-ui/react';
import { EditIcon, CloseIcon } from '@chakra-ui/icons';
import StoresApi from '../../../../../../api/stores';
import PinCodeApi from 'api/pincode';
import { useAuth } from '../../../../../../auth-context/auth.context';
import Card from 'components/card/Card';
import SelectField from '../../../../../../components/formComponents/selectField.js';
import InputField from '../../../../../../components/formComponents/inputField.js';
import { UpdateStoreFormValidationSchema } from 'formValidations/updateStoreFormValidations';
import { Modules } from 'config/constant';
import { usePermissions } from 'contexts/PermissionContext';
import NoPermissionsMessage from 'components/nopermission/NoPermissions';

const StoreDetails = (props) => {
  const { user } = useAuth();
  const toast = useToast();
  const toastIdRef = React.useRef();
  const operationLocationsList = props?.operationLocationsList;
  const companiesList = props?.companiesList;
  const storeData = props?.storeData;
  const storeId = props?.storeId;
  const [inpDisable, setInpDisable] = useState(true);
  const [disabled, setDisabled] = useState(true);
  const enable = () => setDisabled(!disabled);
  const disable = () => {
    setDisabled(true);
    window.location.reload();
  };
  const { getPermissionsForModule } = usePermissions();
  const [storePermissions, setStorePermissions] = useState(null);

  useEffect(() => {
    const storePermissions = getPermissionsForModule([Modules.Store]);
    setStorePermissions(storePermissions);
  }, []);

  console.log('StoreData from Branch update', storeData);

  const initialValues = {
    operationLocationId: storeData.operation_location_id || '',
    companyId: storeData.company_id || '',
    name: storeData.name || '',
    storeCode: storeData.store_code,
    goldLocker: storeData.gold_locker_enable,
    savingPlan: storeData.saving_plan_enable,
    email: storeData.email || '',
    phoneNumber: storeData.phone_number || '',
    countryCode: storeData.country_code || '',
    addressLine1: storeData.address_line1 || '',
    addressLine2: storeData.address_line2 || '',
    city: storeData.city || '',
    state: storeData.state || '',
    country: storeData.country || '',
    landmarks: storeData.landmarks || '',
    pincode: storeData.pincode || '',
    status: storeData.status || '',
  };

  const handleUpdate = async (values, { setSubmitting }) => {
    if (toastIdRef.current) {
      toast.close(toastIdRef.current);
    }
    try {
      let response = await StoresApi.Update({
        storeId,
        ...values,
      });
      response = response.data;
      if (response.status_code === 200) {
        toastIdRef.current = toast({
          title: response.message || 'Successfully Updated',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
          onCloseComplete: () => {
            setTimeout(() => {
              window.location.reload();
            }, 10);
          },
        });
      } else if (response.status_code !== 200) {
        toastIdRef.current = toast({
          title: response.message || 'Error Updating',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      }
    } catch (error) {
      toastIdRef.current = toast({
        title: 'Unable to Update',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      console.log(error);
    } finally {
      setSubmitting(false);
      setDisabled(true);
    }
  };

  const GetPincode = async (e, setFieldValue) => {
    console.log('GetPincode called', e);

    try {
      let response = await PinCodeApi.Get({ pincode: e });
      response = response.data;
      console.log(response);
      if (
        response.status_code === 200 &&
        Object.keys(response.data).length !== 0
      ) {
        setFieldValue('country', response.data.country);
        setFieldValue('state', response.data.state);
        setFieldValue('city', response.data.city);
        setInpDisable(false);
      } else {
        toastIdRef.current = toast({
          title:
            'Pincode not found! Please recheck pincode or fill City, State and Country manually.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
        setInpDisable(false);
      }
    } catch (error) {
      console.log('Here is Error', error);
      setInpDisable(false);
    }
  };
  if (!storePermissions?.read) {
    return <NoPermissionsMessage module={Modules.Store} />;
  }

  return (
    <>
      <Flex alignItems='center' justifyContent='space-between' mt={32} mb={10}>
        <Text fontSize='3xl' fontWeight='400'>
          About - {storeData.name}
        </Text>
        {disabled ? (
          <Tooltip
            label={
              !storePermissions?.update
                ? 'You do not have permission to edit'
                : undefined
            }
            placement='top'
            shouldWrapChildren
            isDisabled={storePermissions?.update} // Disables Tooltip if update permission exists
          >
            <EditIcon
              ml='5'
              fontSize='large'
              color={
                storePermissions?.update && storePermissions?.read
                  ? 'black'
                  : 'gray.400'
              }
              onClick={
                storePermissions?.update && storePermissions?.read
                  ? enable
                  : undefined
              } // Only enables click if update permission exists
              cursor={storePermissions?.update ? 'pointer' : 'not-allowed'}
            />
          </Tooltip>
        ) : (
          <CloseIcon
            ml='5'
            fontSize='large'
            onClick={disable}
            cursor='pointer'
          />
        )}
      </Flex>

      <Formik
        initialValues={initialValues}
        validationSchema={UpdateStoreFormValidationSchema}
        onSubmit={handleUpdate}
        enableReinitialize
      >
        {({ setFieldValue, isSubmitting }) => (
          <Form noValidate>
            <Flex direction='column'>
              <Flex
                direction={{ base: 'column', md: 'column', lg: 'row' }}
                justifyContent='space-between'
                gap={2}
              >
                <SelectField
                  label='Under Operation Location:'
                  required={!disabled && true}
                  disabled={disabled}
                  bgColor={disabled ? 'gray.200' : ''}
                  variant='auth'
                  name='operationLocationId'
                  placeholder='Select Operation Location of Branch'
                  options={operationLocationsList.map((location) => ({
                    value: location.id,
                    label: location.name,
                  }))}
                />

                <SelectField
                  label='Under Company:'
                  required={!disabled && true}
                  disabled={disabled}
                  bgColor={disabled ? 'gray.200' : ''}
                  variant='auth'
                  name='companyId'
                  placeholder='Select Parent Company of Branch'
                  options={companiesList.map((company) => ({
                    value: company.id,
                    label: company.name,
                  }))}
                />
              </Flex>
              <Card bgColor='gray.200' mt={12}>
                <Flex
                  direction={{ base: 'column', md: 'column', lg: 'row' }}
                  gap={2}
                >
                  <Flex width='100%'>
                    <InputField
                      label='Name of the Branch'
                      required={!disabled && true}
                      name='name'
                      type='text'
                      variant='auth'
                      bgColor={disabled ? 'gray.200' : ''}
                      disabled={disabled}
                    />
                  </Flex>

                  <Flex minWidth='240px'>
                    <SelectField
                      label='Advance Book'
                      variant='auth'
                      bgColor={disabled ? 'gray.200' : ''}
                      name='goldLocker'
                      options={[
                        { value: true, label: 'Enable' },
                        { value: false, label: 'Disable' },
                      ]}
                      disabled={disabled}
                    />
                  </Flex>
                  <Flex minWidth='240px'>
                    <SelectField
                      label='Saving Plan'
                      variant='auth'
                      bgColor={disabled ? 'gray.200' : ''}
                      name='savingPlan'
                      options={[
                        { value: true, label: 'Enable' },
                        { value: false, label: 'Disable' },
                      ]}
                      disabled={disabled}
                    />
                  </Flex>
                </Flex>

                <Flex
                  direction={{ base: 'column', md: 'column', xl: 'row' }}
                  gap={2}
                >
                  <Flex
                    direction={{ base: 'column', md: 'column', lg: 'row' }}
                    gap={2}
                    width='100%'
                  >
                    <InputField
                      label='E-Mail Address'
                      required={!disabled && true}
                      name='email'
                      type='email'
                      variant='auth'
                      bgColor={disabled ? 'gray.200' : ''}
                      disabled={disabled}
                    />
                    <InputField
                      label='Phone Number'
                      required={!disabled && true}
                      name='phoneNumber'
                      type='text'
                      variant='auth'
                      bgColor={disabled ? 'gray.200' : ''}
                      disabled={disabled}
                    />
                  </Flex>

                  <Flex
                    direction={{ base: 'column', md: 'column', lg: 'row' }}
                    gap={2}
                    width='100%'
                  >
                    <SelectField
                      label='Status'
                      name='status'
                      variant='auth'
                      bgColor={disabled ? 'gray.200' : ''}
                      options={[
                        { value: 'ACTIVE', label: 'Active' },
                        { value: 'INACTIVE', label: 'Inactive' },
                      ]}
                      disabled={disabled}
                    />

                    <InputField
                      label='Store Code'
                      variant='auth'
                      name='storeCode'
                      type='text'
                      bgColor='gray.200'
                      disabled
                    />
                  </Flex>
                </Flex>
              </Card>

              <Text mt={12} fontSize='xl' fontWeight='700'>
                Postal Address :
              </Text>

              <Card bgColor='gray.200' mt={4}>
                <Flex
                  direction={{ base: 'column', md: 'column', lg: 'row' }}
                  justifyContent='space-between'
                  gap={2}
                >
                  <InputField
                    label='Address Line 1'
                    required={!disabled && true}
                    name='addressLine1'
                    type='text'
                    variant='auth'
                    bgColor={disabled ? 'gray.200' : ''}
                    disabled={disabled}
                  />
                  <InputField
                    label='Address Line 2'
                    name='addressLine2'
                    type='text'
                    variant='auth'
                    bgColor={disabled ? 'gray.200' : ''}
                    disabled={disabled}
                  />
                </Flex>
                <Flex
                  direction={{ base: 'column', md: 'column', lg: 'row' }}
                  justifyContent='space-between'
                  gap={2}
                >
                  <InputField
                    label='Landmarks'
                    required={!disabled && true}
                    name='landmarks'
                    type='text'
                    variant='auth'
                    bgColor={disabled ? 'gray.200' : ''}
                    disabled={disabled}
                  />
                  <InputField
                    label='Pincode'
                    required={!disabled && true}
                    name='pincode'
                    type='number'
                    variant='auth'
                    bgColor={disabled ? 'gray.200' : ''}
                    onChange={(e) => {
                      setFieldValue('pincode', e.target.value);
                      e.target.value.length === 6 &&
                        GetPincode(e.target.value, setFieldValue);
                    }}
                    disabled={disabled}
                  />
                </Flex>
                <Flex
                  direction={{ base: 'column', md: 'column', lg: 'row' }}
                  justifyContent='space-between'
                  gap={2}
                >
                  <InputField
                    label='City'
                    required={!disabled && true}
                    name='city'
                    type='text'
                    variant='auth'
                    bgColor={inpDisable || disabled ? 'gray.200' : ''}
                    disabled={inpDisable || disabled}
                  />
                  <InputField
                    label='State'
                    required={!disabled && true}
                    name='state'
                    type='text'
                    variant='auth'
                    bgColor={inpDisable || disabled ? 'gray.200' : ''}
                    disabled={inpDisable || disabled}
                  />
                  <InputField
                    label='Country'
                    required={!disabled && true}
                    name='country'
                    type='text'
                    variant='auth'
                    bgColor={inpDisable || disabled ? 'gray.200' : ''}
                    disabled={inpDisable || disabled}
                  />
                </Flex>
              </Card>

              {!disabled && (
                <Flex>
                  <Button
                    mt={20}
                    colorScheme='blue'
                    type='submit'
                    disabled={isSubmitting}
                  >
                    Update Branch
                  </Button>
                </Flex>
              )}
            </Flex>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default StoreDetails;
